import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ErrorBoundary from '../../../../components/Common/ErrorBoundary';
//import ChartDataLabels from 'chartjs-plugin-datalabels';


ChartJS.register(ArcElement, Tooltip, Legend);

function generateColorSets(count) {
    const baseColors = [
        ["#d06841", "#fa6028"],
        ["#dd2121", "#fa4646"],
        ["#daa926", "#f7b607"],
        ["#83ba2b", "#96eb0e"],
        ["#2b8eba", "#0ee3eb"],
        ["#7e41d0", "#b728fa"],
        ["#d041a6", "#fa28b7"],
        ["#41d0b6", "#28fae6"],
        ["#d0c541", "#faf728"],
        ["#415ed0", "#28aefa"]
    ];
    // Repeat colors if not enough
    const severityColorMap = {
      Extreme: '#dd2121',
      High: '#d06841',
      Moderate: '#daa926',
      Low: '#83ba2b',
      // Add more if needed
    };
    const sets = [];
    for (let i = 0; i < count; i++) {
        sets.push(baseColors[i % baseColors.length]);
    }
    return sets;
}

const defaultColorSets = [["#d06841","#fa6028"],["#dd2121","#fa4646"],["#daa926","#f7b607"],["#83ba2b","#96eb0e"]];

// Gradient color sets for severity
const severityGradientMap = {
    'Extreme': ['#dd2121', '#fa4646'],
    'High': ['#d06841', '#fa6028'],
    'Moderate': ['#daa926', '#f7b607'],
    'Low': ['#83ba2b', '#96eb0e'],
    'Offline': ['#F32C22', '#FF6B6B'], // Gradient from red to light red
    'Online': ['#7BB526', '#A8E063'], // Gradient from green to light green
    'Non-Integrated': ['#747474', '#A9A9A9'], // Gradient from dark gray to light gray
    'Faulty': ['#f44336', '#FF7961'], // Gradient from red to light red
    'Normal': ['#28a745', '#81C784'], // Gradient from green to light green
    'Unknown': ['#131415', '#4B4B4B'], // Gradient from black to dark gray
    'Integrated': ['#0B70D1', '#63A4FF'], // Gradient from blue to light blue,
    'Unavailable': ['#454545', '#E0E0E0'] // Gradient from dark gray to lighter gray
};

const DonutChart = ({isDarkTheme, DonutWidgetDetails, widgetConfig, handleModalShow, widgetData}) => {
    const canvasRef = useRef(null);
    const [chartData, setChartData] = useState(null);

    const { WidgetData = {} } = widgetData || {};

    const { WidgetName = 'Donut Chart', WidgetID = '', ColorSets = [], Datapoints = [], Entity = '' } = widgetConfig || {};

    const yName = Datapoints && Datapoints.length > 0 ? Datapoints[0] : 'y-axis';
    const xName = Datapoints && Datapoints.length > 1 ? Datapoints[1] : 'x-axis';  

    const labels = useMemo(() => WidgetData[xName] || [], [WidgetData, xName]);
    const data = useMemo(() => WidgetData[yName] || [], [WidgetData, yName]);
    //console.log({labels})

    // Memoize colorSetsToUse
    const colorSetsToUse = useMemo(() => {
        if (ColorSets && ColorSets.length >= data.length) {
            return ColorSets;
        }
        return generateColorSets(data.length);
    }, [ColorSets, data.length]);

    useEffect(() => {
        if (!widgetData || !canvasRef.current) return;
        if (!labels.length || !data.length){
            setChartData(null);
            return;
        }
        const ctx = canvasRef.current.getContext('2d');

        // Use severityGradientMap if labels match severities, else fallback to colorSetsToUse
        const gradients = labels.map((label, i) => {
            const [start, end] = severityGradientMap[label] || colorSetsToUse[i] || ['#cccccc', '#eeeeee'];
            const grad = ctx.createLinearGradient(0, 0, 0, 150);
            grad.addColorStop(0, start);
            grad.addColorStop(1, end);
            return grad;
        });

        setChartData({
            labels: labels,
            datasets: [
                {
                    data: data,
                    backgroundColor: gradients,
                    borderWidth: 2,
                    cutout: '60%',
                },
            ],
        });
    }, [labels, data, colorSetsToUse, widgetData, widgetConfig]);
    
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 14,
                    color: isDarkTheme ? '#555' : '#bababa',
                },
            },
            tooltip: { enabled: true },
            datalabels: {
                color: '#333',
                anchor: 'center',
                align: 'center', // 'end' puts it outside the arc
                offset: 10,
                font: {
                    weight: 'bold',
                    size: 12,
                },
                formatter: (value, context) => value, // Show value
            },
        },
        onClick: (event, elements, chart) => {
            if (elements && elements.length > 0 && chartData) {
                const element = elements[0];
                const index = element.index;
                const filter = {[xName]: chartData.labels[index]}
                // widgetID 15 is Integrated System
                if(WidgetID === 15){
                    filter['IntegrationStatus'] = 'Integrated'
                }
                //handleModalShow && handleModalShow(filter, Entity, WidgetID);

                const today = new Date();
                const yyyy = today.getFullYear();
                const mm = String(today.getMonth() + 1).padStart(2, '0');
                const dd = String(today.getDate()).padStart(2, '0');
                const csvFileName = `${Entity.toLowerCase()}_${chartData.labels[index].toLowerCase()}_data_${yyyy}-${mm}-${dd}`;
                handleModalShow && handleModalShow(filter, Entity, WidgetID, csvFileName);
            }
        },
        onHover: (event, elements) => {
            if (event?.native) {
                event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
            }
        }
    };
    return (
        <ErrorBoundary>
        <div className='systemHealth m-0 WidgetPadding p-0' key={`widget-${WidgetID}`}>
            <p className='chartHeaderP p-2'>{WidgetName}
                { widgetConfig.DateRange
                    ? widgetConfig.DateRange === 1
                        ? ` `
                        : ` - ${widgetConfig.DateRange} Days`
                    : ''}
            </p>
            <div style={{ height: '85%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <canvas ref={canvasRef} style={{ display: 'none' }} /> {/* Used just for gradients */}
            {!chartData ? (
                <span style={{ color: "#888", fontWeight: 500 }}>No Data Available</span>
            ) : (
                <Doughnut data={chartData} options={options}/> // plugins={[ChartDataLabels]}
            )}
            </div>
        </div>
        </ErrorBoundary>
    );
}

export default DonutChart